import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, ExternalLink, DollarSign, Zap, ArrowRight } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

const FundingGuidePage = ({ user, onLogout }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">💰 Super Simple: Get USDT</h1>
        <p className="text-gray-600 mb-8">Just 2 easy steps to start sending money</p>

        {/* What You Need */}
        <Card className="mb-6 border-4 border-emerald-500 bg-emerald-50">
          <CardHeader>
            <CardTitle className="text-2xl">🎯 You ONLY Need USDT</CardTitle>
            <CardDescription className="text-base">No MATIC needed! We handle all fees automatically!</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-6 bg-white rounded-lg border-2 border-emerald-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 text-white">
                  <DollarSign className="w-10 h-10" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-2xl mb-2">Just Buy USDT</h3>
                  <p className="text-lg text-gray-700">
                    That's it! Buy $10-20 of USDT and you're ready to send money.
                  </p>
                </div>
              </div>
              
              <Alert className="bg-green-50 border-green-300">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <AlertDescription className="text-green-800 text-base">
                  <strong>NEW:</strong> We pay ALL gas fees for you! You never need MATIC or understand "gas fees". Just use USDT!
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>

        {/* Step by Step */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">📝 Just 2 Steps!</CardTitle>
            <CardDescription className="text-base">It's really this simple now</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Step 1 */}
              <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border-2 border-emerald-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold flex-shrink-0 text-xl">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-3 text-xl">Buy $10-20 of USDT</h4>
                    <p className="text-base text-gray-700 mb-3">
                      On your phone or computer, search for these services:
                    </p>
                    <div className="space-y-2 mb-4">
                      <p className="text-base text-gray-700">
                        • <strong>MoonPay.com</strong> - Buy with credit card (easiest!)
                      </p>
                      <p className="text-base text-gray-700">
                        • <strong>Coinbase</strong> - If you have an account
                      </p>
                      <p className="text-base text-gray-700">
                        • <strong>Binance</strong> - If you have an account
                      </p>
                    </div>
                    <Alert className="bg-yellow-50 border-yellow-300">
                      <AlertDescription className="text-yellow-800 text-sm">
                        <strong>⚠️ Important:</strong> When buying or withdrawing, make sure it says <strong>"Polygon Network"</strong> (not Ethereum)
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold flex-shrink-0 text-xl">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-3 text-xl">Send USDT to Your RouteFlow Wallet</h4>
                    <p className="text-base text-gray-700 mb-4">
                      Copy your wallet address from RouteFlow and paste it when buying/withdrawing USDT.
                    </p>
                    <Button
                      size="lg"
                      onClick={() => navigate('/crypto/wallet')}
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 text-lg"
                    >
                      Get My Wallet Address
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    <p className="text-sm text-gray-600 mt-4">
                      Wait 2-5 minutes for USDT to arrive, then you're ready to send money!
                    </p>
                  </div>
                </div>
              </div>

              {/* That's It! */}
              <Alert className="bg-green-50 border-green-300 border-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <AlertDescription className="text-green-800 text-lg">
                  <strong>That's it!</strong> No gas fees, no MATIC, no complications. Just USDT and you're ready to send money anywhere!
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>

        {/* Quick Facts */}
        <Card>
          <CardHeader>
            <CardTitle>💡 Why This Is Easy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-gray-700">
                <strong>No gas fees for you:</strong> We pay all transaction fees automatically!
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-gray-700">
                <strong>Super fast:</strong> Money arrives in 2-3 seconds!
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-gray-700">
                <strong>Stable value:</strong> USDT always equals $1 (no price changes!)
              </p>
            </div>
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-gray-700">
                <strong>One currency:</strong> Just USDT - no need for any other tokens!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FundingGuidePage;
